import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import NexGateList from '../components/NexGate/NexGateList';
import NexGateForm from '../components/NexGate/NexGateForm';
import NexGateDetail from '../components/NexGate/NexGateDetail';
import { Button } from '../components/ui/button';
import { 
  FeatherArrowLeft,
  FeatherCheckCircle,
  FeatherAlertCircle
} from '../components/ui/icons';
import { isManager } from '../utils/roleUtils';
import api from '../services/api';
import './NexGate.css';

const NexGate = () => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form', 'detail'
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [userIsManager, setUserIsManager] = useState(false);

  // Check if user has manager role
  useEffect(() => {
    if (user) {
      checkUserRole();
    }
  }, [user]);

  const checkUserRole = async () => {
    try {
      const response = await api.get('/api/auth/role');
      const userRole = response.data?.role;
      const isManagerRole = isManager(userRole);
      console.log('User role:', userRole, 'Is manager:', isManagerRole);
      setUserIsManager(isManagerRole);
    } catch (error) {
      console.error('Error checking user role:', error);
      setUserIsManager(false);
    }
  };

  useEffect(() => {
    // Clear success message after 5 seconds
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCreateNew = () => {
    console.log('handleCreateNew called');
    setSelectedEngagement(null);
    setCurrentView('form');
  };

  const handleViewEngagement = (engagement) => {
    setSelectedEngagement(engagement);
    setCurrentView('detail');
  };

  const handleEditEngagement = (engagement) => {
    console.log('handleEditEngagement called with:', engagement);
    setSelectedEngagement(engagement);
    setCurrentView('form');
  };

  const handleFormSuccess = (data) => {
    setSuccessMessage(
      selectedEngagement 
        ? 'Engagement updated successfully!' 
        : `Engagement created successfully! Reference ID: ${data.nexgate_id}`
    );
    setCurrentView('list');
    setSelectedEngagement(null);
  };

  const handleFormCancel = () => {
    setCurrentView('list');
    setSelectedEngagement(null);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedEngagement(null);
  };

  const renderView = () => {
    switch (currentView) {
      case 'form':
        return (
          <div className="nexgate-page">
            <div className="page-header">
              <Button
                variant="ghost"
                onClick={handleBackToList}
                className="back-button"
              >
                <FeatherArrowLeft size={16} />
                Back to List
              </Button>
            </div>
            <NexGateForm
              engagement={selectedEngagement}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </div>
        );

      case 'detail':
        return (
          <div className="nexgate-page">
            <div className="page-header">
              <Button
                variant="ghost"
                onClick={handleBackToList}
                className="back-button"
              >
                <FeatherArrowLeft size={16} />
                Back to List
              </Button>
            </div>
            <NexGateDetail
              engagement={selectedEngagement}
              onEdit={() => handleEditEngagement(selectedEngagement)}
              onBack={handleBackToList}
            />
          </div>
        );

      default:
        return (
          <div className="nexgate-page">
            {!userIsManager && (
              <div className="access-warning">
                <FeatherAlertCircle size={20} />
                <div>
                  <h3>Limited Access</h3>
                  <p>You have view-only access to NexGate engagements. Only managers can create or edit engagements.</p>
                </div>
              </div>
            )}
            <NexGateList
              onViewEngagement={handleViewEngagement}
              onEditEngagement={userIsManager ? handleEditEngagement : null}
              onCreateNew={userIsManager ? handleCreateNew : null}
            />
            {/* Debug info */}
            <div style={{ display: 'none' }}>
              Debug: isManager={userIsManager.toString()}, onCreateNew={userIsManager ? 'function' : 'null'}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="nexgate-container">
      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          <FeatherCheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {renderView()}
    </div>
  );
};

export default NexGate; 